import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, first_name?: string, last_name?: string) => Promise<void>;
  socialLogin: (provider: string, email: string, token: string, first_name?: string, last_name?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeUser = (data: any): User => ({
  id: data.id ?? data.user_id,
  username: data.username || '',
  email: data.email || '',
  first_name: data.first_name || '',
  last_name: data.last_name || '',
  is_staff: Boolean(data.is_staff),
  is_superuser: Boolean(data.is_superuser),
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;

      const fetchUser = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/user/`);
          setUser(normalizeUser(response.data));
        } catch (error) {
          console.error('Error fetching user:', error);
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        } finally {
          setLoading(false);
        }
      };

      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login/`, {
        username,
        password
      });
      
      const { token: newToken, ...userData } = response.data;
      
      setToken(newToken);
      setUser(normalizeUser(userData));
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Token ${newToken}`;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid username or password');
      }
      throw new Error('Login failed. Please try again.');
    }
  };

  const register = async (username: string, email: string, password: string, first_name = '', last_name = '') => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register/`, {
        username,
        email,
        password,
        first_name,
        last_name
      });
      
      const { token: newToken, ...userData } = response.data;
      
      setToken(newToken);
      setUser(normalizeUser(userData));
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Token ${newToken}`;
    } catch (error: any) {
      if (error.response?.data) {
        const errData = error.response.data;
        let errMsg = 'Registration failed. Please try again.';
        if (typeof errData === 'object') {
          const firstKey = Object.keys(errData)[0];
          const firstVal = errData[firstKey];
          errMsg = Array.isArray(firstVal) ? firstVal[0] : JSON.stringify(firstVal);
          if (firstKey !== 'non_field_errors') {
            errMsg = `${firstKey.charAt(0).toUpperCase() + firstKey.slice(1)}: ${errMsg}`;
          }
        }
        throw new Error(errMsg);
      }
      throw new Error('Registration failed. Please try again.');
    }
  };

  const socialLogin = async (provider: string, email: string, tokenVal: string, first_name = '', last_name = '') => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/social-login/`, {
        provider,
        email,
        token: tokenVal,
        first_name,
        last_name
      });
      
      const { token: newToken, ...userData } = response.data;
      
      setToken(newToken);
      setUser(normalizeUser(userData));
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Token ${newToken}`;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(`${provider} Sign-In failed. Please try again.`);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    login,
    register,
    socialLogin,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
